import Button from "@edaz/edazkit/Button/index";
import { IconType } from "@edaz/edazkit/Icon";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import type { ViewComponent } from "@frui.ts/views";
import { preventDefault, registerView } from "@frui.ts/views";
import type InternalReportResponse from "entities/internalReportResponse";
import ReportRequestStatus from "entities/reportRequestStatus";
import { observer } from "mobx-react-lite";
import React from "react";
import { busyIdentificator } from "utils/busyWatcher";
import { formatDate, formatDateTime, translateEnum, translateGeneral } from "utils/localizations";
import ListView from "views/common/list";
import BusinessReportsListViewModel from "viewModels/timeCharges/reporting/internalReports/businessReports/businessReportsListViewModel";
import Form from "react-bootstrap/Form";
import FormGroup from "controls/formGroup";
import { Col, FormLabel, Row } from "react-bootstrap";
import DatePickerBox from "controls/datePickerBox";
import { startOfEDAZDistribution } from "config";
import { czechTodayDate, disabledDaysRange } from "utils/dateUtils";

const BusinessReportsListView: ViewComponent<BusinessReportsListViewModel> = observer(({ vm }) => {
  const columns: ColumnDefinition<InternalReportResponse>[] = [
    {
      title: vm.translateAttribute("fileName"),
      property: "fileName",
      cellProps: _ => ({ style: { width: 400 } }),
    },
    {
      title: vm.translateAttribute("fromDate"),
      valueFormatter: props => formatDate(props.value),
      property: "fromDate",
    },
    {
      title: vm.translateAttribute("processedAt"),
      valueFormatter: props => formatDateTime(props.value),
      property: "processedAt",
    },
    {
      title: vm.translateAttribute("createdBy"),
      property: "createdBy",
      cellProps: _ => ({ style: { width: 150 } }),
    },
    {
      title: vm.translateAttribute("status"),
      property: "status",
      valueFormatter: props => translateEnum("reportRequestStatus", props.value),
      cellProps: _ => ({ style: { width: 200 } }),
    },
  ];

  const actionButtons = (item: InternalReportResponse) => {
    const isBusy = vm.busyWatcher.checkBusy(busyIdentificator([item]));

    return (
      <>
        {item.status === ReportRequestStatus.Processed && (
          <Button
            variant="success"
            size="sm"
            className="rounded"
            onClick={() => vm.onDownload(item)}
            disabled={isBusy}
            iconLeft={isBusy ? IconType.Spinner : IconType.Download}>
            {vm.translate("downloadFile")}
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <Form className="bg-light mb-4 p-4 rounded" onSubmit={preventDefault(vm.filterData)}>
        <Row>
          <FormGroup as={Col} sm={6} md={4} target={vm.filterItem} property="fromDate">
            <FormLabel>{vm.translate("fromReportDay")}</FormLabel>
            <DatePickerBox
              getInputValue={vm.setInputValidSince}
              fromMonth={startOfEDAZDistribution}
              toMonth={czechTodayDate()}
              disabledDays={disabledDaysRange(czechTodayDate(), startOfEDAZDistribution)}
              placeholder={translateGeneral("choose")}
            />
          </FormGroup>
          <FormGroup as={Col} sm={6} md={4} target={vm.filterItem} property="toDate">
            <FormLabel>{vm.translate("toReportDay")}</FormLabel>
            <DatePickerBox
              getInputValue={vm.setInputValidUntil}
              fromMonth={startOfEDAZDistribution}
              toMonth={czechTodayDate()}
              disabledDays={disabledDaysRange(czechTodayDate(), startOfEDAZDistribution)}
              placeholder={translateGeneral("choose")}
            />
          </FormGroup>
          <Form.Group as={Col} sm={6} md={4}>
            <Button iconLeft={IconType.Search} className="mt-md-5 rounded btn" type="submit" disabled={vm.busyWatcher?.isBusy}>
              {translateGeneral("search")}
            </Button>
            {(vm.filterItem.fromDate || vm.filterItem.toDate) && (
              <Button
                iconLeft={IconType.Cross}
                variant="outline-danger"
                onClick={vm.onResetClick}
                className="mt-md-5 rounded btn"
                type="reset">
                {vm.translate("resetFilter")}
              </Button>
            )}
          </Form.Group>
        </Row>
      </Form>
      <ListView vm={vm} columns={columns} showDetailWithList={true} hideTitle={true} actionButtons={actionButtons} />
    </>
  );
});

registerView(BusinessReportsListView, BusinessReportsListViewModel);
